import React, { useEffect, useState } from 'react';
import { useEthers } from '@usedapp/core';
import { Grid, Button, InputLabel } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { Form } from '../hooks/useForm';
import { ColorPicker, ColorPalette, createColor } from 'material-ui-color';
import { useAllowance, useMint, useMemori } from '../hooks';
import { mintFee } from '../utils/feeUtils';
import { textPalette, backgroundPalette } from '../utils/colorUtils';
import { useDispatch, useSelector } from 'react-redux';
import { mintNft, cacheNft } from '../features/editor/editorSlice';
import { getNextId } from '../features/nft/nftSlice';

const width = 360;
const height = 360;
const tileSize = 20;
const alphanumeric = /^[\p{L}\p{N}]*$/u;

export default function CustomForm({
  textId,
  text,
  setOpenPopup,
  setSnackBarMessage,
  setShowSnackBar,
}) {
  const dispatch = useDispatch();
  // Web3 hooks
  const { account } = useEthers();
  const [address, abi, chainId] = useMemori();
  const { mintWrapper, mintState } = useMint(address, abi);
  const { nextId } = useSelector((store) => store.nft);
  const allowance = useAllowance(address, abi, account);

  // Show backdrop when connecting to metamask (during minting)
  const [showBackdrop, setShowBackdrop] = useState(false);

  // NFT properties
  const [textColor, setTextColor] = useState(createColor('green'));
  const [backgroundColor, setBackgroundColor] = useState(createColor('black'));
  const [fee, setFee] = useState(mintFee[chainId]);
  const validate = () => true;

  console.log(
    `Allowance ${allowance}; chainId ${chainId}; nextId: ${nextId}; contract: ${address}`
  );

  useEffect(() => {
    dispatch(getNextId({ contract: address }));
  }, [address, dispatch]);

  const handleSubmit = (e) => {
    if (validate()) {
      setShowBackdrop(true);
      mint();
    }
    e.preventDefault();
  };

  const setTextColorFromPalette = (value) => {
    setTextColor(createColor(value));
  };

  const setBackgroundFromPallete = (value) => {
    setBackgroundColor(createColor(value));
  };

  const mint = async () => {
    setShowBackdrop(true);

    dispatch(
      mintNft({
        nextId,
        textId,
        chainId,
        address,
        account,
        textColor: textColor.hex,
        backgroundColor: backgroundColor.hex,
      })
    ).then(({ payload }) => {
      console.log(payload);
      mintWrapper(account, payload.tokenURI, fee);
    });
  };

  useEffect(() => {
    setFee(allowance === 0 ? mintFee[chainId] : '0');
  }, [chainId, allowance]);

  useEffect(() => {
    if (mintState.status === 'Success') {
      setShowBackdrop(false);
      setSnackBarMessage(`Minted NFT#${nextId}`);
      setShowSnackBar(true);
      setOpenPopup(false);
      dispatch(cacheNft({ _nftId: nextId, chainId, contract: address }));
      dispatch(getNextId());
    } else if (mintState.status === 'Exception') {
      setShowBackdrop(false);
      setSnackBarMessage(`Mint failed: ${mintState.errorMessage}`);
      setShowSnackBar(true);
    }
  }, [
    mintState,
    address,
    chainId,
    nextId,
    setOpenPopup,
    setShowSnackBar,
    setSnackBarMessage,
    dispatch,
  ]);

  React.useEffect(() => {
    (() => {
      var canvas = document.getElementById('preview');
      var context = canvas.getContext('2d');
      context.clearRect(0, 0, width, height);

      const background = backgroundColor.rgb.join(', ');
      context.fillStyle = `rgb(${background})`;
      context.fillRect(0, 0, width, height);

      context.font = '100pt courier';
      context.textAlign = 'center';
      context.textBaseline = 'top';

      const id = nextId.toString(36).toUpperCase();

      var theme = textColor.rgb.join(', ');
      context.fillStyle = `rgba( ${theme} , 0.8)`;
      context.fillText(id, width / 2, height / 3);

      const rows = Math.ceil(width / tileSize);
      const cols = Math.ceil(height / tileSize);

      context.font = tileSize - 2 + 'px courier';
      for (var i = 0; i <= rows * cols; i++) {
        var row = Math.floor(i / cols);
        var col = i - row * cols;
        var text_index = i % text.length;
        if (row <= rows) {
          if (text[text_index].match(alphanumeric)) {
            context.font = tileSize - 2 + 'px courier';
          }
          context.fillText(
            text[text_index],
            10 + col * tileSize,
            tileSize * row
          );
        }
      }
    })();
  }, [textColor, backgroundColor, text, nextId]);

  return (
    <Form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <canvas
            id='preview'
            width='360'
            height='360'
            style={{
              border: '1px solid #d3d3d3',
              padding: 0,
              margin: 'auto',
              display: 'block',
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <InputLabel style={{ marginLeft: '0.5em' }}>Text</InputLabel>
          <ColorPalette
            palette={textPalette}
            onSelect={setTextColorFromPalette}
          />
          <ColorPicker
            disableAlpha
            value={textColor}
            onChange={setTextColor}
            defaultValue='green'
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <InputLabel style={{ marginLeft: '0.5em' }}>Background</InputLabel>
          <ColorPalette
            palette={backgroundPalette}
            onSelect={setBackgroundFromPallete}
          />
          <ColorPicker
            disableAlpha
            value={backgroundColor}
            onChange={setBackgroundColor}
            defaultValue='black'
          />
        </Grid>

        <Grid item xs={12}>
          <Button color='inherit' variant='outlined' type='submit'>
            Mint
          </Button>
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={showBackdrop}
          >
            <CircularProgress color='inherit' />
          </Backdrop>
        </Grid>
      </Grid>
    </Form>
  );
}
